import { Component, OnInit, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { VesselAnnouncementService } from '../../core/api/vessel-announcement.service';
import { VesselAnnouncementListItem } from '../../shared/models/vessel-announcement';
import { statusTone } from '../../shared/status-tone';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { StatusLabelPipe } from '../../shared/status-label.pipe';
import { MneDatePipe } from '../../shared/mne-date.pipe';

type ViewMode = 'card' | 'table';

@Component({
  standalone: true,
  imports: [RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe, StatusLabelPipe, MneDatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'va.title' | t }}</h1>
      </header>

      <div class="filter-bar">
        <select [value]="statusFilter()" (change)="statusFilter.set($any($event.target).value)">
          <option value="">{{ 'filters.allStatuses' | t }}</option>
          @for (s of statusOptions(); track s) { <option [value]="s">{{ s | statusLabel }}</option> }
        </select>
        @if (statusFilter()) {
          <button type="button" class="filter-clear" (click)="statusFilter.set('')">{{ 'filters.clear' | t }}</button>
        }
      </div>

      <button type="button" class="view-toggle" (click)="toggleView()">
        @if (viewMode() === 'card') {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="4" rx="1"/><rect x="3" y="10" width="18" height="4" rx="1"/><rect x="3" y="16" width="18" height="4" rx="1"/></svg>
          {{ 'ta.viewTable' | t }}
        } @else {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/></svg>
          {{ 'ta.viewCards' | t }}
        }
      </button>

      @if (loading()) {
        <div class="skeleton-list">
          @for (i of [1,2,3,4,5,6]; track i) { <div class="skeleton-row"></div> }
        </div>
      }
      @if (error()) { <p class="error notice">{{ error() | t }}</p> }

      @if (!loading() && viewMode() === 'card') {
        <section class="list">
          @for (item of filtered(); track item.Id) {
            <div class="time-card" [class]="'tone-' + tone(item.StatusName)">
              <div class="time-card-top">
                <span class="row-id">#{{ item.Id }}</span>
                <span class="status-pill" [class]="tone(item.StatusName)">{{ item.StatusName | statusLabel }}</span>
              </div>
              <span class="time-card-ref">{{ item.VesselName || ('va.fallback' | t) }}</span>
              @if (item.CustomerName) { <div class="time-card-cargo">{{ item.CustomerName }}</div> }
              <div class="time-card-times">
                <div class="time-block">
                  <span class="time-value">{{ (item.EstimatedArrivalDate | mneDate) }}</span>
                  <span class="time-date">{{ 'va.estimatedArrival' | t }}</span>
                </div>
                <div class="time-card-sep">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                </div>
                <div class="time-block">
                  <span class="time-value">{{ (item.EstimatedDepartureDate | mneDate) }}</span>
                  <span class="time-date">{{ 'va.estimatedDeparture' | t }}</span>
                </div>
              </div>
              <a class="time-card-more" [routerLink]="['/vessel-announcements', item.Id]">{{ 'ta.seeMore' | t }} ›</a>
            </div>
          } @empty { @if (!error()) {
            <div class="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7h16v13H4z"/><path d="M4 7l3-4h10l3 4"/><path d="M9 11h6"/></svg>
              <span>{{ 'va.empty' | t }}</span>
            </div>
          } }
        </section>
      }

      @if (!loading() && viewMode() === 'table' && filtered().length) {
        <div class="data-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ 'va.title' | t }}</th>
                <th>{{ 'va.customer' | t }}</th>
                <th>{{ 'va.estimatedArrival' | t }} – {{ 'va.estimatedDeparture' | t }}</th>
                <th>{{ 'common.status' | t }}</th>
              </tr>
            </thead>
            <tbody>
              @for (item of filtered(); track item.Id) {
                <tr [class]="'tone-' + tone(item.StatusName)" [routerLink]="['/vessel-announcements', item.Id]">
                  <td class="cell-id">#{{ item.Id }}</td>
                  <td>{{ item.VesselName || ('va.fallback' | t) }}</td>
                  <td class="cell-muted">{{ item.CustomerName || '—' }}</td>
                  <td class="cell-muted">{{ (item.EstimatedArrivalDate | mneDate:false) }} – {{ (item.EstimatedDepartureDate | mneDate:false) }}</td>
                  <td><span class="status-pill" [class]="tone(item.StatusName)">{{ item.StatusName | statusLabel }}</span></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else if (!loading() && viewMode() === 'table' && !error()) {
        <div class="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7h16v13H4z"/><path d="M4 7l3-4h10l3 4"/><path d="M9 11h6"/></svg>
          <span>{{ 'va.empty' | t }}</span>
        </div>
      }
    </main>
  `
})
export class VesselAnnouncementsPage implements OnInit {
  readonly items = signal<VesselAnnouncementListItem[]>([]);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly statusFilter = signal('');
  readonly viewMode = signal<ViewMode>(this.readStoredView());

  readonly statusOptions = computed(() =>
    Array.from(new Set(this.items().map(i => i.StatusName).filter((v): v is string => !!v))).sort()
  );

  readonly filtered = computed(() => {
    const status = this.statusFilter();
    return status ? this.items().filter(i => i.StatusName === status) : this.items();
  });

  constructor(private readonly api: VesselAnnouncementService) {}

  ngOnInit(): void {
    this.api.search({ Page: 1, PageSize: 50 }).subscribe({
      next: items => { this.items.set(items); this.loading.set(false); },
      error: err => { this.error.set(err?.error?.Message ?? err?.error?.message ?? 'va.loadError'); this.loading.set(false); }
    });
  }

  tone(status?: string | null): string { return statusTone(status); }

  toggleView(): void {
    const next: ViewMode = this.viewMode() === 'card' ? 'table' : 'card';
    this.viewMode.set(next);
    localStorage.setItem('va.viewMode', next);
  }

  private readStoredView(): ViewMode {
    return localStorage.getItem('va.viewMode') === 'table' ? 'table' : 'card';
  }
}
